<template>
  <div>
    <form @submit.prevent="form.submitForm">
      <template v-if="!form.isMultiTripMode.value">
        <SearchDirectionInputs
          :dep="form.route.value.dep"
          :arr="form.route.value.arr"
          class="base-shadow"
          :source="props.source"
          :validation="form.validation$.value"
          @onToInputClick="form.showToSheet(0)"
          @onFromInputClick="form.showFromSheet(0)"
          @onSwap="form.swapCities(0)"
        />

        <div class="mt-2 flex gap-2">
          <SearchDateRangeInputRedesigned
            class="inputs-block-shadow"
            :dates="form.route.value.dates"
            :source="props.source"
            @onDateSelect="form.openCalendar(0, $event)"
          />
          <SearchPassengersInputRedesigned
            class="inputs-block-shadow"
            :is-youth="form.searchForm.isYouth.value"
            :cabin-class="form.searchForm.cabinClass.value"
            :passengers="form.searchForm.passengers.value"
            :source="props.source"
            @onInputClick="form.openPassengersSheet"
          />
        </div>
      </template>

      <template v-else>
        <MultiTripSearchPanel
          :source="props.source"
          :routes="form.routesList.value"
          :get-direction-validation="form.getDirectionValidation"
          :can-add-route="form.canAddMoreRoutes.value"
          :show-add-another="form.canAddMoreRoutes.value"
          :add-another-label-key="multiTripAddAnotherLabelKey"
          @onFromSheet="form.showFromSheet"
          @onToSheet="form.showToSheet"
          @onSwap="form.swapCities"
          @onOpenCalendar="form.openCalendar"
          @onAddLeg="form.onAddAnotherLeg"
          @onOpenMultiTripInfo="onOpenMultiTripInfo"
          @onRemoveLeg="form.removeLeg"
        />

        <SearchPassengersInputRedesigned
          class="inputs-block-shadow mt-2"
          :is-youth="form.searchForm.isYouth.value"
          :cabin-class="form.searchForm.cabinClass.value"
          :passengers="form.searchForm.passengers.value"
          :source="props.source"
          @onInputClick="form.openPassengersSheet"
        />
      </template>

      <FreeButton
        type="submit"
        class="green-main-button mt-6 w-full"
      >
        {{ $t('main.findTickets') }}
      </FreeButton>

      <MultiTripSearchEntry
        class="mt-2"
        :disabled="multiTripToggleDisabled"
        @onClick="onMultiTripToggleClick"
      >
        <template #icon>
          <RouteIcon v-if="!form.isMultiTripMode.value" class="h-6 w-6" />
          <ReturnIcon v-else class="h-6 w-6" />
        </template>
        {{ $t(multiTripToggleLabelKey) }}
      </MultiTripSearchEntry>
    </form>

    <PassengersSelectBottomSheet
      :cabin-class="form.searchForm.cabinClass.value"
      :passengers="form.searchForm.passengers.value"
      :is-youth="form.searchForm.isYouth.value"
      :name="form.bottomSheetIds.passengers"
      :source="props.source"
      @onCabinClassChange="form.updateCabinClass"
      @onSetIsYouth="form.setIsYouth"
      @onPassengersStateChange="form.updatePassengersState"
    />
    <SearchCalendar
      :cabin-class="form.searchForm.cabinClass.value"
      :arr="form.calendarRoute.value.arr?.code"
      :dep="form.calendarRoute.value.dep?.code"
      :is-youth="form.searchForm.isYouth.value"
      :passengers="form.searchForm.passengers.value"
      :selected-dates="form.calendarRoute.value.dates"
      :multi-trip-mode="form.isMultiTripMode.value"
      :min-date="form.calendarMinDate.value"
      :max-date="form.calendarMaxDate.value"
      :marked-dates="form.calendarMarkedDates.value"
      :name="form.bottomSheetIds.calendar"
      @onDatesSelected="form.updateDates"
      @onSelectAirline="form.selectedAirline.value = $event"
    />
    <CitySelectBottomSheetsContainer
      :cities-bottom-sheet-to="form.bottomSheetIds.citiesTo"
      :cities-bottom-sheet-from="form.bottomSheetIds.citiesFrom"
      :source="props.source"
      @onArrSelect="form.updateCity($event, 'arr')"
      @onDepSelect="form.updateCity($event, 'dep')"
    />
    <Teleport to="body">
      <UnifiedSearchFlowSheet
        v-if="form.isAnimatedFlow.value && !form.isMultiTripMode.value"
        :name="form.bottomSheetIds.animatedFlow"
        :source="props.source"
        :initial-field="form.animatedFlowInitialField.value"
        :dep="form.route.value.dep ?? null"
        :arr="form.route.value.arr ?? null"
        :dates="form.route.value.dates ?? []"
        :passengers="form.searchForm.passengers.value"
        :cabin-class="form.searchForm.cabinClass.value"
        :is-youth="form.searchForm.isYouth.value"
        @onCitySelect="(field, city) => form.updateCity(city, field)"
        @onDatesCommit="form.updateDates"
        @onCabinClassChange="form.updateCabinClass"
        @onSetIsYouth="form.setIsYouth"
        @onPassengersStateChange="form.updatePassengersState"
        @onSelectAirline="form.selectedAirline.value = $event"
        @onSubmitFromSheet="form.submitForm"
      />
    </Teleport>
    <SearchFormErrorModal
      :name="form.bottomSheetIds.errorModal"
    />
    <MultiTripInfoBottomSheet :name="form.bottomSheetIds.multiTripInfo" />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { FreeButton, SearchDirectionInputs } from 'freedom-shared';

  import { PassengersSelectBottomSheet } from '@/widgets/passengers-select-bottom-sheet';
  import { SearchCalendar } from '@/widgets/search-calendar';

  import { analyticsEventsLib } from '@/shared/lib';
  import type { MainPageVariant } from '@/shared/lib';

  import RouteIcon from '@/assets/images/multi-trip/route-icon-24x24.svg?component';
  import ReturnIcon from '@/assets/images/multi-trip/return-icon-24x24.svg?component';

  import { useAviaSearchForm } from '../model/use-avia-search-form';
  import MultiTripSearchEntry from './MultiTripSearchEntry.vue';
  import MultiTripSearchPanel from './MultiTripSearchPanel.vue';
  import SearchDateRangeInputRedesigned from './SearchDateRangeInputRedesigned.vue';
  import SearchPassengersInputRedesigned from './SearchPassengersInputRedesigned.vue';
  import CitySelectBottomSheetsContainer from './CitySelectBottomSheetsContainer.vue';
  import SearchFormErrorModal from './SearchFormErrorModal.vue';
  import MultiTripInfoBottomSheet from './MultiTripInfoBottomSheet.vue';
  import UnifiedSearchFlowSheet from './UnifiedSearchFlowSheet.vue';

  const props = withDefaults(
    defineProps<{
      source: string;
      variant?: MainPageVariant;
    }>(),
    { variant: undefined },
  );

  const emit = defineEmits<{
    onSearchFormSubmit: [searchParams: string, query?: string];
  }>();

  const form = useAviaSearchForm(
    () => props.source,
    emit,
    () => props.variant,
  );

  const multiTripAddAnotherLabelKey = 'main.multiTrip.addAnother';

  const multiTripToggleLabelKey = computed(() =>
    form.isMultiTripMode.value
      ? 'main.multiTrip.backToSimple'
      : 'main.multiTrip.entryLink',
  );

  const multiTripToggleDisabled = computed(
    () => !form.isMultiTripMode.value && !form.canAddMoreRoutes.value,
  );

  const onMultiTripToggleClick = () => {
    if (form.isMultiTripMode.value) {
      form.exitMultiTripMode();
    } else {
      form.enterMultiTripMode();
    }
  };

  const onOpenMultiTripInfo = () => {
    analyticsEventsLib.multiTripInfoOpen({
      source: props.source,
      legs_count: form.routesList.value.length,
    });
    form.bottomSheet.show(form.bottomSheetIds.multiTripInfo);
  };

  defineExpose({
    activateMultiTripSearch: form.activateMultiTripSearch,
  });
</script>
